import { useCombobox } from 'downshift';
import { useRef, useState, Fragment, useMemo } from 'react';

import { CloseOutlineIc, SearchOutlineIc } from '@dsch/dd-icons';

import {
  InputButton,
  InputButtonProps,
} from '../../../../components/Toolkit/InputButton/InputButton';
import { Text } from '../../../../components/ToolkitV2/Text/Text';

import {
  ListItem,
  InputWrapper,
  SearchIcon,
  KeywordSearchInput,
  ClearButton,
  Heading,
  List,
} from './KeywordSearch.styled';

import {
  SearchableDropdown,
  highlight,
} from '../../../../components/ToolkitV2/SearchableDropdown/SearchableDropdown';

import type { TextProps } from '../../../../components/ToolkitV2/Text/Text.typed';
import type { MakeModelOption } from '../../MakeModelKeywordSearch.typed';
import { DisplayContainerConfig } from '../../../../components/Support/DisplayContainer/DisplayContainer';
import { MakeModelKeywordSearchModal } from '../MakeModelKeywordSearchModal/MakeModelKeywordSearchModal';
import { theme } from '../../../../helpers/Theme';
import ResponsiveDisplay from '../ResponsiveDisplay/ResponsiveDisplay';
import { useMakeModelKeywordSearch } from './KeywordSearch.hook';

type DownshiftKeywordSearchProps = {
  value: string;
  onSelect: (option: MakeModelOption) => void;
  initialItems: Array<MakeModelOption>;
  modalDisplayConfig: DisplayContainerConfig;
  dropdownDisplayConfig: DisplayContainerConfig;
  minPrice?: string;
  maxPrice?: string;
  minYear?: string;
  maxYear?: string;
};

/**
 * This component is responsible for displaying the UI and managing its logic in an accessible manner.
 *
 * It contains a modal and dropdown. Each of these can be configured to be displayed or hidden on different screen sizes.
 * They both contain list items that display makes or make and model options.
 *
 * useCombobox from downshift is used to managed the internal state of the dropdown and list items for both and the modal and the dropdown.
 * As a result, the input of the component is consistent across screen sizes.
 *
 * The state of the modal is managed separately. React modal requires its own state for displaying the modal with the list and dismissing it.
 */
export const KeywordSearch = (props: DownshiftKeywordSearchProps) => {
  const {
    value,
    onSelect,
    initialItems,
    modalDisplayConfig,
    dropdownDisplayConfig,
    minPrice,
    maxPrice,
    minYear,
    maxYear,
  } = props;

  const toggleButtonRef = useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = useState<string>('');

  const { data: makeModelOptions } = useMakeModelKeywordSearch({
    minPrice,
    maxPrice,
    minYear,
    maxYear,
    keyword: inputValue,
  });

  const items = useMemo(
    () => (inputValue === '' ? initialItems : makeModelOptions ?? []),
    [inputValue, initialItems, makeModelOptions],
  );

  const {
    isOpen,
    getInputProps,
    getMenuProps,
    getToggleButtonProps,
    getItemProps,
    highlightedIndex,
    getLabelProps,
    openMenu,
    closeMenu,
  } = useCombobox({
    items,
    itemToString: (item) => item?.label ?? '',
    initialInputValue: value,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect(selectedItem);
    },
    inputValue,
    onInputValueChange: ({ inputValue }) => setInputValue(inputValue),
    stateReducer(_state, actionAndChanges) {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
          // Without this, the dropdown would close when an item from the initial list of makes is selected.
          toggleButtonRef.current?.focus();
          // Without this, the dropdown would close when the searchbox is selected.
          return {
            ...changes,
            isOpen: true,
          };
        // Without this, the dropdown would close when an item from the initial list of makes is selected.
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: items === initialItems,
          };
        // Without this, the focus is lost when an item is selected/dropdown is closed.
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          toggleButtonRef.current?.focus();
          return changes;
        default:
          return changes;
      }
    },
  });

  const TEXT_PROPS: TextProps = {
    size: 'M16',
    forwardedAs: 'span',
  };

  const KEYWORD_SEARCH_INPUT_PROPS = {
    type: 'search',
    placeholder: 'Start typing a make or model',
    'aria-label': 'Keyword search input',
  };

  const INPUT_BUTTON_PROPS: InputButtonProps = {
    placeholder: 'All makes/models',
    isActive: false,
    heightVariant: 'LARGE',
  };

  const ListItems = () => (
    <>
      {items.map((item, index) => {
        const { label, count, groupLabel } = item;
        return (
          <Fragment key={`${label}-${index}`}>
            {groupLabel && (
              <Heading weight="bold" color="GREY_DARKER" size="M16">
                {groupLabel}
              </Heading>
            )}
            <ListItem
              {...getItemProps({
                item,
                index,
              })}
              highlighted={highlightedIndex === index}
            >
              {
                <Text {...TEXT_PROPS} color="GREY_DARKER">
                  {highlight({
                    item: label,
                    searchText: inputValue,
                  })}
                </Text>
              }
              {count ? (
                <Text {...TEXT_PROPS} color="GREY_DARK">
                  {' '}
                  ({count})
                </Text>
              ) : (
                ''
              )}
            </ListItem>
          </Fragment>
        );
      })}
    </>
  );

  return (
    <>
      {/* Modal
        NB: We're using ResponsiveDisplay instead of DisplayContainer to make sure
        we don't have the two components mounted on the DOM at the same time.
        Indeed if that is the case we would have problems with refs because
        we're using the same downshift props for both. */}
      <ResponsiveDisplay
        small={modalDisplayConfig.small === 'block'}
        medium={modalDisplayConfig.medium === 'block'}
        large={modalDisplayConfig.large === 'block'}
      >
        <InputButton
          onClick={openMenu}
          value={inputValue}
          aria-label="Open make & model modal"
          {...INPUT_BUTTON_PROPS}
        />
        <MakeModelKeywordSearchModal isOpen={isOpen} onRequestClose={closeMenu}>
          <label {...getLabelProps()}>
            <InputWrapper
              margin={`${theme.donedeal.spacing.S8} ${theme.donedeal.spacing.M16} 0`}
            >
              <SearchIcon aria-label="Search">
                <SearchOutlineIc />
              </SearchIcon>
              <KeywordSearchInput
                {...KEYWORD_SEARCH_INPUT_PROPS}
                {...getInputProps()}
              />
              {inputValue && (
                <ClearButton
                  onClick={() => {
                    setInputValue('');
                  }}
                  aria-label="Clear search"
                  ofType="GHOST"
                >
                  <CloseOutlineIc width={16} height={16} />
                </ClearButton>
              )}
            </InputWrapper>
          </label>
          <List {...getMenuProps()}>{isOpen && <ListItems />}</List>
        </MakeModelKeywordSearchModal>
      </ResponsiveDisplay>

      {/* Dropdown */}
      <ResponsiveDisplay
        small={dropdownDisplayConfig.small === 'block'}
        medium={dropdownDisplayConfig.medium === 'block'}
        large={dropdownDisplayConfig.large === 'block'}
      >
        {!isOpen && (
          <InputButton
            value={inputValue}
            aria-label="Toggle make & model dropdown"
            {...INPUT_BUTTON_PROPS}
            {...getToggleButtonProps({ tabIndex: 0 })}
          />
        )}
        <SearchableDropdown isOpen={isOpen}>
          <label {...getLabelProps()}>
            <InputWrapper>
              <SearchIcon aria-label="Search">
                <SearchOutlineIc />
              </SearchIcon>
              <KeywordSearchInput
                {...KEYWORD_SEARCH_INPUT_PROPS}
                {...getInputProps()}
              />
              {inputValue && (
                <ClearButton
                  onClick={() => {
                    setInputValue('');
                  }}
                  aria-label="Clear search"
                  ofType="GHOST"
                >
                  <CloseOutlineIc width={16} height={16} />
                </ClearButton>
              )}
            </InputWrapper>
          </label>
          <List
            {...getMenuProps({ height: '337px' })}
            borderRadius={`0 0 ${theme.donedeal.borderRadius.default} ${theme.donedeal.borderRadius.default}`}
          >
            {isOpen && <ListItems />}
          </List>
        </SearchableDropdown>
      </ResponsiveDisplay>
    </>
  );
};
